// "A new version is ready" — the update counterpart to InstallAppBanner.
//
// It deliberately looks like the install offer and sits in the same slot: the
// same shape of message, one clear action, one way out. A shopkeeper who has
// installed the app already knows what this strip means.
//
// Two differences from the install banner, both on purpose:
//
//   · It is shown at every width, not only on a phone. An update matters to
//     whoever is running the app, and the admin dashboard is used on a laptop.
//
//   · Dismissing it lasts for this visit only. The install offer hides for 30
//     days because declining it is a real answer; declining an update is not --
//     the old build stays old. So it steps out of the way now and asks again
//     next launch.

import { useCallback, useEffect, useState } from 'react';
import { IconX, IconRefreshCw } from './Icons';
import { subscribeToUpdates, applyUpdate } from './pwaUpdate';

export default function UpdateAppBanner() {
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeToUpdates(setReady), []);

  const update = useCallback(() => {
    if (busy) return;
    setBusy(true);
    // No success state to render: applying the update reloads the page, so the
    // only thing this component can usefully say is that it started.
    applyUpdate();
    // If the reload does not come (the worker vanished, another tab won the
    // race), release the button rather than leaving it stuck on "Updating".
    setTimeout(() => setBusy(false), 6000);
  }, [busy]);

  if (!ready || dismissed) return null;

  return (
    <div className="pwa-install pwa-update" role="status" aria-live="polite" aria-label="Update available">
      <span className="pwa-install-icon pwa-update-icon" aria-hidden="true">
        <IconRefreshCw size={22} />
      </span>

      <div className="pwa-install-text">
        <strong className="pwa-install-title">A new version is ready</strong>
        <span className="pwa-install-origin">Update to get the latest AcyMart</span>
      </div>

      <button type="button" className="pwa-install-cta" onClick={update} disabled={busy}>
        {busy ? 'Updating…' : 'Update'}
      </button>

      {/* For this visit only -- see the note at the top of the file. */}
      <button
        type="button"
        className="pwa-install-close"
        onClick={() => setDismissed(true)}
        aria-label="Not now — update later"
      >
        <IconX size={15} />
      </button>
    </div>
  );
}
